import { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { C, F, SiteHeader, SiteFinalCta, SiteFooter } from '../components/SiteChrome';
import { BlogCard, formatPostDate } from '../components/BlogCard';
import PostBody from '../components/PostBody';
import FaqList from '../components/FaqList';
import PostSlides from '../components/PostSlides';
import ImageLightbox from '../components/ImageLightbox';
import { fetchPost, fetchPosts, imageSrc } from '../lib/blogApi';
import { usePageSeo, pageTitle } from '../lib/seo';

const SITE = 'https://mimique.app';

// The body split at the end of its Nth section, where the slides go. A section
// is an H2 heading and the text under it, so the slides land just before the
// next H2. Text before the first heading is not a section (0 puts the slides
// right after it). A post with fewer sections than that gets them at the end.
const splitAfterSection = (html, n) => {
    let at = -1;
    for (let i = 0; i <= n; i += 1) {
        at = html.indexOf('<h2', at + 1);
        if (at === -1) return [html, ''];
    }
    return [html.slice(0, at), html.slice(at)];
};

/**
 * One blog post, loaded from the API by the slug in the URL.
 *
 * Posts are edited at /admin/blog and stored in the database, so this route
 * matches any top-level path and asks the backend whether it is a post. It
 * sits last in the route table, under every real page, and sends anything
 * unrecognised back to the landing page.
 */
const BlogPost = () => {
    const { slug } = useParams();
    const { pathname } = useLocation();
    // Which post was loaded, and which address it was loaded for. The two are
    // kept together on purpose: clearing the post inside the effect leaves one
    // render where the new address is paired with the previous post, and the
    // canonical redirect below reads that as a wrong address and bounces
    // straight back, which is what stopped links between posts working.
    const [loaded, setLoaded] = useState({ slug: null, post: undefined, failed: false });
    const [related, setRelated] = useState([]);
    const post = loaded.slug === slug ? loaded.post : undefined;
    // The request itself failed (a network problem, the server down), which is
    // not the same as the post not existing. Only a real 404 sends the reader
    // home; a failure used to do that too, and a search engine rendering the page
    // at that moment recorded the post as a redirect to the homepage.
    const failed = loaded.slug === slug && loaded.failed;

    useEffect(() => {
        let live = true;
        fetchPost(slug)
            .then((doc) => { if (live) setLoaded({ slug, post: doc, failed: false }); })
            .catch(() => { if (live) setLoaded({ slug, post: undefined, failed: true }); });
        fetchPosts()
            .then((list) => { if (live) setRelated(list.filter((p) => p.slug !== slug)); })
            .catch(() => { /* the sidebar is a nicety; a failure here is not worth an error */ });
        return () => { live = false; };
    }, [slug]);

    if (post === null) return <Navigate to="/" replace />;
    // The post answered to an address that is not its own: a name it used to
    // have, or /blog/<slug>. Send the reader, and any search engine following
    // the old link, on to the real one.
    if (post && pathname !== `/${post.slug}`) return <Navigate to={`/${post.slug}`} replace />;
    return <PostPage post={post} related={related} failed={failed} />;
};

const PostPage = ({ post, related, failed }) => {
    const canonical = post ? `${SITE}/${post.slug}` : SITE;
    const faqs = (post && post.published && post.faqs) || [];
    // The picture open full size, if any: a list (the slides can be paged
    // through) and which one is showing.
    const [lightbox, setLightbox] = useState(null);
    const openOne = (src, alt) => setLightbox({ images: [{ src, alt }], index: 0 });

    usePageSeo({
        // Until the post is here there is nothing true to say, so the head the
        // server already wrote is left alone instead of being swapped for a blank
        // title and a noindex tag.
        enabled: !!post,
        title: pageTitle(post?.metaTitle || post?.title),
        description: post?.metaDescription || '',
        canonical,
        // A post that has not been published yet is linked from the ones that
        // have, so it stays reachable, but it should not turn up in search.
        noindex: !post || !post.published,
        jsonLd: post && post.published ? [{
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.metaDescription,
            datePublished: post.date,
            image: post.image ? `${SITE}${post.image}` : undefined,
            author: { '@type': 'Person', name: "Leanne O'Leary", url: 'https://leanneoleary.com/' },
            publisher: { '@type': 'Organization', name: 'Mimique', url: SITE },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
        }, ...(faqs.length ? [{
            // The same questions the page shows, for search and AI answers.
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map(({ q, a }) => ({
                '@type': 'Question',
                name: q,
                acceptedAnswer: { '@type': 'Answer', text: a },
            })),
        }] : [])] : null,
    });

    return (
        <div style={{ minHeight: '100vh', background: '#ffffff', color: C.deep, fontFamily: F.body }}>
            <SiteHeader />

            <div className="mq-postlayout" style={{ maxWidth: 1160, margin: '0 auto', padding: '56px 24px 90px', display: 'grid', gap: 56, alignItems: 'start' }}>
                <article>
                    {!post ? (
                        <div style={{ color: C.light, fontSize: 15 }}>{failed ? 'This post could not be loaded. Please refresh the page to try again.' : 'Loading...'}</div>
                    ) : (
                        <>
                            {/* The square picture with the date and title beside it, the
                                post itself underneath. Stacks on a phone. Which part of
                                the picture shows is set in the blog editor. */}
                            <div className="mq-posthead" style={{ marginBottom: 32 }}>
                                {post.image && (
                                    <img
                                        className="mq-postimg"
                                        src={imageSrc(post.image)}
                                        alt={post.imageAlt || ''}
                                        onClick={() => openOne(imageSrc(post.image), post.imageAlt)}
                                        style={{
                                            cursor: 'zoom-in', display: 'block', aspectRatio: '1 / 1',
                                            borderRadius: 24, objectFit: 'cover', objectPosition: post.imagePosition || 'center',
                                        }}
                                    />
                                )}
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 600, color: C.light }}>
                                        {formatPostDate(post.date)} · {post.readTime}
                                    </div>
                                    <h1 className="blog-h1" style={{ margin: 0, fontFamily: F.heading, fontSize: 38, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, color: C.deep }}>
                                        {post.title}
                                    </h1>
                                </div>
                            </div>

                            {post.published ? (
                                (() => {
                                    const bodyStyle = { fontSize: 16.5, lineHeight: 1.8, color: C.bodyOnWhite2 };
                                    const slides = post.slides || [];
                                    if (!slides.length) return <PostBody className="blog-body" html={post.body} style={bodyStyle} onImageClick={openOne} />;
                                    const [before, after] = splitAfterSection(post.body || '', post.slidesAfter ?? 1);
                                    return (
                                        <>
                                            {before && <PostBody className="blog-body" html={before} style={bodyStyle} onImageClick={openOne} />}
                                            <PostSlides slides={slides} onOpen={(i) => setLightbox({ images: slides.map((x) => ({ src: imageSrc(x.src), alt: x.alt })), index: i })} />
                                            {after && <PostBody className="blog-body" html={after} style={bodyStyle} onImageClick={openOne} />}
                                        </>
                                    );
                                })()
                            ) : (
                                // Linked from the posts already live, so the link
                                // works from day one instead of pointing at nothing.
                                <div className="blog-body" style={{ fontSize: 16.5, lineHeight: 1.8, color: C.bodyOnWhite2 }}>
                                    <p>This one goes live on {formatPostDate(post.date)}.</p>
                                    <p>{post.excerpt}</p>
                                </div>
                            )}

                            {/* Questions and answers, written per post in the blog
                                editor and sent to search as FAQPage data as well. */}
                            {faqs.length > 0 && (
                                <section style={{ marginTop: 48 }}>
                                    <h2 style={{ margin: '0 0 18px', fontFamily: F.heading, fontSize: 24, fontWeight: 800, color: C.deep, letterSpacing: '-0.01em' }}>
                                        Questions people ask
                                    </h2>
                                    <FaqList faqs={faqs} />
                                </section>
                            )}

                            {/* Sign-off, written per post in the blog editor. */}
                            {/* Styled like the sidebar cards (white, thin border, rounded
                                square picture) so it reads as different from the FAQ. */}
                            <div style={{ marginTop: 44, padding: 14, background: '#ffffff', border: `1px solid ${C.borderLight}`, borderRadius: 20, display: 'flex', alignItems: 'center', gap: 14 }}>
                                <img
                                    src="/assets/founder.jpg"
                                    alt="Leanne O'Leary, Mimique founder"
                                    style={{ width: 76, height: 76, borderRadius: 14, objectFit: 'cover', flexShrink: 0 }}
                                />
                                <PostBody className="blog-bio" html={post.bio} style={{ fontSize: 14.5, lineHeight: 1.65, color: C.bodyOnWhite2 }} />
                            </div>
                        </>
                    )}
                </article>

                {/* Sidebar. Sticky on desktop so the other posts stay in view
                    down a long read; it drops under the article on narrow
                    screens, where it reads as a related-posts block instead. */}
                {related.length > 0 && (
                    <aside className="mq-postside">
                        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.primary, marginBottom: 16 }}>
                            More posts
                        </div>
                        <div style={{ display: 'grid', gap: 14 }}>
                            {related.map((p) => <BlogCard key={p.slug} post={p} compact />)}
                        </div>
                    </aside>
                )}
            </div>

            <SiteFinalCta compact />

            {lightbox && (
                <ImageLightbox
                    images={lightbox.images}
                    index={lightbox.index}
                    onIndex={(i) => setLightbox((l) => ({ ...l, index: i }))}
                    onClose={() => setLightbox(null)}
                />
            )}

            <SiteFooter />

            <style>{`
                .blog-body h2 {
                    line-height: 1.2;
                    font-family: ${F.heading};
                    font-size: 24px;
                    font-weight: 800;
                    color: ${C.deep};
                    letter-spacing: -0.01em;
                    margin: 40px 0 14px;
                }
                .blog-body h3 {
                    line-height: 1.25;
                    font-family: ${F.heading};
                    font-size: 19px;
                    font-weight: 800;
                    color: ${C.deep};
                    margin: 30px 0 10px;
                }
                .blog-body p { margin: 0 0 18px; }
                .blog-body ul, .blog-body ol { margin: 0 0 18px; padding-left: 22px; }
                .blog-body li { margin-bottom: 8px; }
                .blog-body blockquote { margin: 0 0 18px; padding-left: 18px; border-left: 3px solid ${C.borderLight}; font-style: italic; }
                .blog-body img { cursor: zoom-in; display: block; max-width: 100%; height: auto; border-radius: 18px; margin: 28px 0; }
                .mq-posthead { display: flex; align-items: center; gap: 28px; }
                .mq-postimg { width: 240px; flex-shrink: 0; }
                @media (max-width: 600px) {
                    .mq-posthead { flex-direction: column; align-items: flex-start; gap: 18px; }
                    .mq-postimg { width: 100%; max-width: 360px; }
                }
                .blog-bio p { margin: 0; }
                .blog-body a, .blog-bio a { color: ${C.primary}; text-decoration: underline; cursor: pointer; }
                .blog-body a:hover, .blog-bio a:hover { color: ${C.mid}; }

                /* Article plus a sidebar the width of one compact card. The
                   article column is capped at a readable measure rather than
                   filling the space the sidebar leaves. */
                .mq-postlayout { grid-template-columns: minmax(0, 760px) minmax(0, 300px); }
                /* Keeps the reading measure once the sidebar drops below. */
                .mq-postlayout article { max-width: 760px; }
                .mq-postside { position: sticky; top: 96px; }
                @media (max-width: 1000px) {
                    .mq-postlayout { grid-template-columns: minmax(0, 1fr); gap: 64px !important; }
                    .mq-postside { position: static; }
                }

                /* A post title runs longer than a section heading, so it comes
                   down a size on a phone where the landing page's 38px headings
                   do not. !important because the sizes being overridden are
                   inline styles on the elements themselves. */
                @media (max-width: 600px) {
                    .blog-h1 { font-size: 30px !important; line-height: 1.1 !important; }
                    .blog-body { font-size: 16px !important; }
                    .blog-body h2 { font-size: 21px; margin-top: 32px; }
                }
            `}</style>
        </div>
    );
};

export default BlogPost;
